import React, { useEffect, useState } from 'react'
import { Button, Title } from '@arvoreeducacao/bonsai-ds'
import { useTranslation } from 'i18n'
import type { LanguageTokens } from 'i18n'
import {
  ButtonWrapper,
  CenterWrapper,
  Wrapper,
  RecommendationText,
  ReadingGoalTimeWrapper,
  RecommendationExplanation,
  RoundButton,
  MinutesSelectionWrapper,
  MinutesSelectionText,
  TimeInput,
  DownloadGuideLink,
  RoundButtonText
} from './SecondStep.style'
import type { SecondStepProps } from '../NewReadingGoalModal.types'
import useDownloadFile from 'utils/hooks/use-download-file'
import { Cdn } from 'utils/assets'
import { trackTimeUsageReportGuideDownloaded } from 'utils/analytics/analytics'
import { post_event } from 'utils/analytics'

const SecondStep: React.FC<SecondStepProps> = ({
  handlePreviousStep,
  handleNextStep,
  editing,
  originalGoal,
  setSelectedReadingGoalTime,
  selectedReadingGoalTime,
  handleEditionGoBack,
  handleSubmit,
  submitting
}) => {
  const { t } = useTranslation()
  const [readingGoalTime, setReadingGoalTime] = useState(originalGoal || 60)

  const isMinusButtonDisabled = readingGoalTime <= 20
  const isPlusButtonDisabled = readingGoalTime >= 300
  const isSaveDisabled = editing && readingGoalTime === originalGoal

  useEffect(() => {
    setSelectedReadingGoalTime(readingGoalTime)
  }, [readingGoalTime, setSelectedReadingGoalTime])

  const handleChangeReadingGoalTime = (number: number) => {
    setReadingGoalTime(number)
  }

  const downloadGuide = useDownloadFile({
    url: Cdn.getImageUrl('reports/orientation-guide-screen-usage-oct-31.pdf'),
    fileNameWithExtension:
      'Árvore - Guia de Orientação Árvore para o uso de telas.pdf'
  })

  return (
    <Wrapper>
      <Title size={3}>
        {t('Defina a nova meta de leitura' as LanguageTokens)}
      </Title>
      <RecommendationText>
        {t(
          'Recomendamos no mínimo 60 minutos semanais para EFAI e 90 minutos para EFAF/EM.'
        )}
      </RecommendationText>

      <CenterWrapper>
        <ReadingGoalTimeWrapper>
          <RoundButton
            onClick={() => handleChangeReadingGoalTime(readingGoalTime - 10)}
            disabled={isMinusButtonDisabled}
          >
            <RoundButtonText>{t('-' as LanguageTokens)}</RoundButtonText>
          </RoundButton>
          <MinutesSelectionWrapper>
            <TimeInput
              type="number"
              value={readingGoalTime}
              min={20}
              max={300}
              disabled
            />
            <MinutesSelectionText>{t('minutos/semana')}</MinutesSelectionText>
          </MinutesSelectionWrapper>
          <RoundButton
            onClick={() => handleChangeReadingGoalTime(readingGoalTime + 10)}
            disabled={isPlusButtonDisabled}
          >
            <RoundButtonText>{t('+' as LanguageTokens)}</RoundButtonText>
          </RoundButton>
        </ReadingGoalTimeWrapper>

        <RecommendationExplanation>
          {t(
            'Para entender melhor nossas recomendações, confira o guia completo sobre o uso de tela nas escolas. '
          )}
          <DownloadGuideLink
            onClick={() => {
              trackTimeUsageReportGuideDownloaded()
              post_event('tutorial_clicked', 'guia_uso_telas')
              downloadGuide.downloadFile()
            }}
          >
            {t('Clique aqui')}
          </DownloadGuideLink>
          {t(' para baixar.')}
        </RecommendationExplanation>
      </CenterWrapper>

      <ButtonWrapper>
        <Button
          theme={{ type: 'secondary', size: 'large' }}
          text={t('Voltar')}
          disabled={!selectedReadingGoalTime}
          onClick={() => {
            if (editing) {
              handleEditionGoBack()
              return
            }
            handlePreviousStep()
          }}
        />
        <Button
          theme={{ type: 'primaryLivros', size: 'large' }}
          text={
            editing
              ? t('Salvar nova meta' as LanguageTokens)
              : t('Revisar meta')
          }
          disabled={!selectedReadingGoalTime || isSaveDisabled}
          loading={submitting}
          onClick={() => {
            if (editing) {
              handleSubmit()
              return
            }

            handleNextStep()
          }}
        />
      </ButtonWrapper>
    </Wrapper>
  )
}

export default SecondStep
